import type BaseApp from '../App';
import type { EaiwsBasket } from '../EaiwsBaskets';
import type { GeometryShareFormat} from '../Share';
import type { ArticleElement, ArticleManager} from '@easterngraphics/wcf/modules/cf';
import type { EaiwsContext } from '@easterngraphics/wcf/modules/eaiws';
import type { OFMLUpdateState, UpdateBasketArticleResult } from '@easterngraphics/wcf/modules/eaiws/basket';
import type { FetchOptions, LockContext } from '@easterngraphics/wcf/modules/utils/async';
import type { HashFileInfo } from '@egr/xbox/app-api/Filesystem/Wrapper';
import type { ShareTask } from '@egr/xbox/share/ShareTask';
import type { FileFormatInfo, GeometryExportFormatKey, ImageExportFormatKey} from '@egr/xbox/utils/FileFormats';
import type { EaiwsGeometryExportSetting } from '@egr/xbox/utils/GeometryExportFormats';
import type { EaiwsImageExportSetting} from '@egr/xbox/utils/ImageExportFormats';

import bind from 'bind-decorator';
import i18next from 'i18next';

import { isGeometryShareFormat } from '../Share';

import { messageManager } from '@egr/xbox/messagebar/MessageManager';
import { withProgress } from '@egr/xbox/progress-manager/ProgressManager';
import { ShareManager } from '@egr/xbox/share/ShareManager';
import { type ShareContent, ShareContentSource } from '@egr/xbox/share/Types';
import { launchIOSQuicklook, launchAndroidModelViewer } from '@egr/xbox/utils/AR';
import { assertNotNull, assert } from '@egr/xbox/utils/Debug';
import { GeometryExportFormat, FileFormats, ImageExportFormat } from '@egr/xbox/utils/FileFormats';
import { EaiwsGeometryExportSettingToOption } from '@egr/xbox/utils/GeometryExportFormats';
import { callOnlyOnce } from '@egr/xbox/utils/Helper';
import { EaiwsImageExportSettingToOption } from '@egr/xbox/utils/ImageExportFormats';
import { TaskCanceledError } from '@egr/xbox/utils/Promise';
import { notNull } from '@egr/xbox/utils/Types';
import { addBreadcrumb } from '@egr/xbox/utils/errors/Breadcrumb';
import { captureException } from '@egr/xbox/utils/errors/Error';

import { MainArticleElement, UserArticleElement } from '@easterngraphics/wcf/modules/cf';
import { DeleteItemsOptions, UpdateBasketArticlesOptions, ItemSelectionOptions } from '@easterngraphics/wcf/modules/eaiws/basket';
import { Lock } from '@easterngraphics/wcf/modules/utils/async';
import { CustomError } from '@easterngraphics/wcf/modules/utils/error';
import { isNotNullOrEmpty, isNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';

// The `SharePayloadEntry` interface actually belongs in the `app-api' sub module,
// but this would be the only reason for the dependency to the `app-api'.
// Therefore it was moved into this file.
export interface SharePayloadEntry extends Partial<HashFileInfo> {
    action: 'fetch' | 'decode' | 'path';
    content: string;
    mimetype: string;
    filename: string;
    remove_path_after_share?: boolean;
}

export class ExportFailed extends CustomError {
    public constructor(public readonly exportFormat: GeometryExportFormatKey) {
        super(`Export failed: ${exportFormat}`);

        Object.setPrototypeOf(this, ExportFailed.prototype);
    }
}

export const enum GeometryExportCheck {
    /** will abort export if not all articles are included */
    SILENT = 1,
    /** should show dialog where user can export even if not all articles will be included */
    WITH_QUESTION = 2
}

interface ExportPreCheckOptions {
    showMessage: boolean

    itemIds?: Array<string>;
    expectedCount?: number;
    /** use for OBX export */
    allowUserArticleElements?: boolean;
}

interface GetGeometryOptions {
    itemIds?: Array<string>;
    check?: GeometryExportCheck;
}

interface GetGeometryUrlParameter {
    format: GeometryExportFormatKey;
    filename?: string;
}

export class ExportManager<App extends BaseApp = BaseApp> {
    public static fetchOptions: FetchOptions = {
        timeout: 120000,
        retryAttempts: 0,
    };

    /**
     * override for specific eaiws-setting on geometry exports
     */
    protected optionOverride(
        value: GeometryExportFormatKey,
        inputSetting: EaiwsGeometryExportSetting,
        alternativeBasket?: EaiwsBasket<EaiwsContext>,
    ): EaiwsGeometryExportSetting {
        return inputSetting;
    }

    protected imageOptionOverride(
        format: ImageExportFormatKey,
        inputValue: EaiwsImageExportSetting
    ): EaiwsImageExportSetting {
        return inputValue;
    }

    protected exportLock: Lock = new Lock();
    protected readonly deleteOptions: DeleteItemsOptions = new DeleteItemsOptions();
    protected readonly updateBasketArticleOptions: UpdateBasketArticlesOptions = new UpdateBasketArticlesOptions();

    public constructor(protected app: App) {
        this.startWebAR_Android = this.startWebAR_Android.bind(this);
        this.startWebAR_IOS = this.startWebAR_IOS.bind(this);

        this.deleteOptions.subItems = true;

        this.updateBasketArticleOptions.migrate = false;
        this.updateBasketArticleOptions.update = false;

        this.registerShareContents();
    }

    public async getGeometryUrl(
        format: GetGeometryUrlParameter,
        options?: GetGeometryOptions,
        alternativeBasket?: EaiwsBasket<EaiwsContext>,
    ): Promise<string | undefined> {
        const basket = alternativeBasket ?? this.app.eaiwsBaskets.basket;
        if (options?.check) {
            const articleManager = await basket.createArticleManager(this.app.coreApp);

            if (
                !(await this.exportPreCheck(
                    articleManager,
                    {
                        itemIds: options.itemIds,
                        showMessage: options.check === GeometryExportCheck.WITH_QUESTION
                    }
                ))
            ) {
                return;
            }
        }

        const result: Array<string> | undefined = await this.getGeometryUrls(
            [format],
            options?.itemIds,
            alternativeBasket,
        );

        return result != null ? result[0] : undefined;
    }

    public async getGeometryUrls(
        formats: Array<GetGeometryUrlParameter>,
        itemIds?: ReadonlyArray<string>,
        alternativeBasket?: EaiwsBasket<EaiwsContext>,
    ): Promise<Array<string> | undefined> {
        const basket = alternativeBasket ?? this.app.eaiwsBaskets.basket;

        if (formats.filter(({format}) => !assertNotNull(GeometryExportFormat[format])).length) {
            return;
        }

        const [cleanupFunction, setId] = await this.prepareExport(itemIds, basket);
        if (cleanupFunction == null || setId == null) {
            return undefined;
        }

        try {
            const result: Array<string> = [];
            for (const exportValue of formats) {
                try {
                    const url: string = await basket.context.basket.getExportedGeometry(
                        setId,
                        EaiwsGeometryExportSettingToOption(
                            this.optionOverride(
                                exportValue.format,
                                GeometryExportFormat[exportValue.format].eaiwsSettings,
                                alternativeBasket
                            )
                        ),
                    );
                    if (isNullOrEmpty(url)) {
                        addBreadcrumb({
                            category: 'Export failed',
                            message: 'empty geometry url'
                        });
                        throw new ExportFailed(exportValue.format);
                    }
                    if (exportValue.filename) {
                        result.push(`${url}?contentDisposition=attachment&filename=${encodeURIComponent(exportValue.filename)}`);
                    } else {
                        result.push(url);
                    }
                } catch (error) {
                    if (!(error instanceof ExportFailed)) {
                        captureException(error, 'debug');
                    }
                    throw new ExportFailed(exportValue.format);
                }
            }
            return result;
        } finally {
            cleanupFunction();
        }
    }

    public async getGeometryShareContentEntries(
        formats: Array<GeometryExportFormatKey>,
        filenameGenerator: (extension: string, format: string) => string,
        itemIds?: ReadonlyArray<string>,
        alternativeBasket?: EaiwsBasket<EaiwsContext>,
    ): Promise<Array<ShareContent>> {
        const payloads: Array<ShareContent> = [];

        const getGeometryUrlParameters: Array<GetGeometryUrlParameter & { filename: string, fileInfo: FileFormatInfo }> = formats.map((value) => {
            const fileInfo: FileFormatInfo | undefined = FileFormats.get(value);
            if (!fileInfo) {
                return null;
            }
            return {
                format: value,
                filename: filenameGenerator(fileInfo.extension, value),
                fileInfo
            };
        }).filter(notNull);

        if (assert(getGeometryUrlParameters.length === formats.length)) {
            const urls: Array<string> | undefined = await this.getGeometryUrls(getGeometryUrlParameters, itemIds, alternativeBasket);
            if (
                urls != null &&
                assert(urls.length === getGeometryUrlParameters.length)
            ) {
                for (let i: number = 0; i < getGeometryUrlParameters.length; i++) {
                    payloads.push({
                        shareFormatKey: getGeometryUrlParameters[i].format as GeometryShareFormat,
                        source: ShareContentSource.REMOTE_URL,
                        content: urls[i],
                        filename: getGeometryUrlParameters[i].filename,
                        filetype: getGeometryUrlParameters[i].fileInfo,
                    });
                }
            }
        }
        return payloads;
    }

    @withProgress('getImageUrl')
    public async getImageUrl(
        basket: EaiwsBasket<EaiwsContext>,
        format: ImageExportFormatKey,
        itemId?: string,
        checkArticles: boolean = false,
        settings?: EaiwsImageExportSetting
    ): Promise<string | undefined> {
        if (checkArticles) {
            const articleManager= await basket.createArticleManager(this.app.coreApp);

            if (!(await this.exportPreCheck(articleManager, { showMessage: false }))) {
                return;
            }
        }

        const [cleanupFunction, setId] = await this.prepareExport(
            itemId ? [itemId] : [],
            basket
        );

        if (cleanupFunction == null || setId == null) {
            return;
        }

        try {
            if (cleanupFunction == null || setId == null) {
                return;
            }
            const url: string = await this.app.eaiwsBaskets.session.basket.getGeneratedImage(
                setId,
                EaiwsImageExportSettingToOption(
                    settings ?? this.imageOptionOverride(format, ImageExportFormat[format].eaiwsSettings)
                ),
            );
            return url;
        } finally {
            cleanupFunction();
        }
    }

    @withProgress('startWebAR iOS')
    public async startWebAR_IOS(): Promise<void> {
        try {
            const usdExport: string | undefined = await this.getGeometryUrl({ format: 'USDZ' });

            if (isNullOrEmpty(usdExport)) {
                throw new Error('Export failed');
            }

            launchIOSQuicklook(usdExport);
        } catch {
            messageManager.showMessage(i18next.t('Export failed'));
        }
    }

    @withProgress('startWebAR Android')
    public async startWebAR_Android(): Promise<void> {
        try {
            const glbExport: string | undefined = await this.getGeometryUrl({ format: 'GLB' });

            if (isNullOrEmpty(glbExport)) {
                throw new Error('Export failed');
            }

            launchAndroidModelViewer(glbExport);
        } catch {
            messageManager.showMessage(i18next.t('Export failed'));
        }
    }

    /**
     * returns true if all articles will be included in export
     *
     * Warning: will update articles with unknown updateState if possible
     * @param articleManager
     */
    public async exportPreCheck(articleManager: ArticleManager, options?: ExportPreCheckOptions): Promise<boolean> {
        const exportableArticles: Array<MainArticleElement> = await this.getExportableMainArticles(
            articleManager,
            options?.itemIds,
            options?.allowUserArticleElements
        );

        const check: number = options?.itemIds?.length ?? options?.expectedCount ?? articleManager.getAllMainArticles().length;

        if (check === 0) {
            messageManager.showMessage(`${i18next.t('Export failed')} (${i18next.t('The product data available does not support the exporting of this article.', { count: check })})`);
            return false;
        }
        return (
            (exportableArticles.length === check) ||
            (options?.showMessage === true && (await this.showCADDownloadPrompt(exportableArticles.length, check)) === true)
        );
    }

    @bind
    protected async *shareExports(shareTask: ShareTask, formats: ReadonlyArray<string>): AsyncIterableIterator<ShareContent> {
        if (shareTask.isCanceled) {
            return;
        }

        const cadFormats = formats.map(
            (value: string): GeometryExportFormatKey => value as GeometryExportFormatKey
        );

        const payloadEntries = await this.getGeometryShareContentEntries(
            cadFormats,
            (extension: string, format: string): string => shareTask.shareFilenameManager.getFilename(
                extension, 'cad_symbols', undefined, format === '3DS' ? '_3DS' : undefined
            ),
            shareTask.itemIds,
            this.app.eaiwsBaskets.getBasket(shareTask.getOptions().basketIdentifier)
        );

        for (const entry of payloadEntries) {
            const {...payloadData} = entry;
            shareTask.telemetryEventQueue?.addPartialEvent({name: payloadData.shareFormatKey});
            yield payloadData;
        }
    }

    protected async prepareExport(
        itemIds?: ReadonlyArray<string>,
        alternativeBasket?: EaiwsBasket<EaiwsContext>,
    ): Promise<[VoidFunction, string, string | null] | [null, null, null]> {
        const basket = alternativeBasket ?? this.app.eaiwsBaskets.basket;
        const context: LockContext = await this.exportLock.acquireLock();

        const selectOptions: ItemSelectionOptions = new ItemSelectionOptions();
        selectOptions.wholeComposite = true;
        selectOptions.subItems = true;

        const basketInstance = basket.context.basket;
        try {
            const articleManager = await basket.createArticleManager(this.app.coreApp);
            const articles: Array<MainArticleElement> = await this.getExportableMainArticles(articleManager, itemIds);

            switch (articles.length) {
                case 0:
                    context.unlock();
                    return [null, null, null];
                case 1:
                    if (isNotNullOrEmpty(articles[0].basketId)) {
                        return [ () => context.unlock(), articles[0].basketId, null];
                    }
            }

            const obxUrl: string = await articleManager.exportObx(articles);
            const folderId: string = await basketInstance.insertFolder(null, null, 'Set');
            const setId: string = await basketInstance.convertToSetArticle(folderId);
            const pastedIds: Array<string> = await basketInstance.paste(setId, null, obxUrl);
            await basketInstance.addToSetArticle(pastedIds);

            const cleanupFunction: VoidFunction = (): void => {
                // Note: it is not save to await the following promise
                void basketInstance.deleteItems([setId], this.deleteOptions).catch((error: Error) => captureException(error, 'debug'));

                context.unlock();
            };
            return [cleanupFunction, setId, obxUrl];
        } catch (error) {
            context.unlock();

            throw error;
        }
    }

    protected async getExportableMainArticles(articleManager: ArticleManager, itemIds?: ReadonlyArray<string>, allowUserArticleElements: boolean = false): Promise<Array<MainArticleElement>> {
        const result: Array<MainArticleElement> = [];

        await articleManager.synchronizeSession(true);

        let articles: Array<MainArticleElement> = [];

        if (itemIds?.length) {
            for (const itemId of itemIds) {
                const article: ArticleElement | null = articleManager.getArticleById(itemId);
                if (article instanceof MainArticleElement) {
                    articles.push(article);
                }
            }
        } else {
            articles = articleManager.getAllMainArticles();
        }

        const unknownState = new Map<string, MainArticleElement>();

        for (const article of articles) {
            if (allowUserArticleElements && article instanceof UserArticleElement && article.basketId != null) {
                result.push(article);
            } else if (!(article instanceof UserArticleElement) && article.basketId != null) {
                const state: OFMLUpdateState = article.getKnownOfmlUpdateState();
                switch (state) {
                    case 'Updatable':
                    case 'UpToDate':
                        result.push(article);
                        break;
                    case 'Unknown':
                        unknownState.set(article.basketId, article);
                        break;
                    default:
                        break;
                }
            }
        }

        const eaiwsStates: Array<UpdateBasketArticleResult> = await this.app.eaiwsBaskets.session.basket.updateBasketArticles(
            Array.from(unknownState.keys()), null, this.updateBasketArticleOptions
        );
        for (const state of eaiwsStates) {
            const article: MainArticleElement | undefined = unknownState.get(state.itemId);
            if (article == null) {
                continue;
            }

            if (state.updateState === 'UpToDate' || state.updateState === 'Updatable') {
                result.push(article);
            }
        }

        return result;
    }

    protected async showCADDownloadPrompt(articleCount: number, count: number): Promise<boolean | undefined> {
        if (articleCount === 0) {
            messageManager.showMessage(`${i18next.t('Export failed')} (${i18next.t('The product data available does not support the exporting of this article.', { count })})`);
        } else if (await this.app.dialogManager.showModalDialog(
            {
                title: i18next.t('Warning'),
                subTitle: [i18next.t('The export does not contain all visible items.')]
            },
            'CAD Download prompt'
        )) {
            return true;
        };

        throw new TaskCanceledError();
    }

    @callOnlyOnce
    private registerShareContents() {
        const shareContents = Object.entries(GeometryExportFormat).map(entry => {
            const [key, value] = entry;
            if (value instanceof GeometryExportFormat && isGeometryShareFormat(key)) {
                return key;
            }

            return;
        }).filter((item): item is GeometryShareFormat => !!item);
        // register the callback used for creating the CAD content
        ShareManager.registerShareContent(
            // lookup all valid export keys and map them to `cadXXX` where `XXX`
            // is the key/upper case file extension
            // e.g. `cadDWG`
            shareContents,
            this.shareExports,
            {
                requiresBasketUpdate: true,
                requiresArticleUpdate: true
            }
        );
    }
}

/*
Used for options in share menus

(usdz, glb)
t('3D Model ({{type}})', { type })

(other geometries)
t('CAD-Model ({{type}})', { type })

*/